// Slack Monitoring Service
//
// Architecture: client-side only, no hosted backend.
//   - OAuth2 Authorization Code + PKCE via Slack's API
//   - Polls channels:history for new messages since last cursor
//   - Stores messages + cursors in IndexedDB
//   - Posts summaries/alerts via chat.postMessage (bot token)
//
// Required OAuth scopes:
//   channels:history     — read public channel messages
//   channels:read        — list public channels
//   groups:history       — read private channel messages (where bot is member)
//   groups:read          — list private channels (where bot is member)
//   users:read           — resolve user IDs to names
//   users:read.email     — optionally map to git author emails
//   chat:write           — post summaries and alerts
//   reactions:read       — read reaction counts
//   channels:join        — join public channels so the bot can read them
//
// Users must create their own Slack App at api.slack.com/apps and install it
// to their workspace. VeriGit stores the resulting bot token (xoxb-...).

import { proxiedFetch } from './cors-proxy';
import { toLocalDateStr } from '@/lib/utils';
import { detectSentimentAnomalies } from './messaging-sentiment';
import { getSlackSettings, saveSlackSettings, saveMessages, getMessagesByDay } from './messaging-storage';
import type {
  SlackSettings,
  MessagingChannel,
  MessagingMember,
  MessagingMessage,
  MessageReaction,
  ChannelActivityStats,
  MemberActivityStats,
  MessagingAnomaly,
  MessagingSnapshot,
} from './messaging-types';

// ─── Constants ───────────────────────────────────────────────────────────────

const SLACK_API = 'https://slack.com/api';

/** How many messages to fetch per channel per poll */
const PAGE_LIMIT = 200;

// ─── Helpers ─────────────────────────────────────────────────────────────────

async function slackGet<T = unknown>(
  token: string,
  method: string,
  params: Record<string, string> = {},
): Promise<T> {
  const url = new URL(`${SLACK_API}/${method}`);
  for (const [k, v] of Object.entries(params)) url.searchParams.set(k, v);
  const resp = await proxiedFetch(url.toString(), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) throw new Error(`Slack API ${method} → HTTP ${resp.status}`);
  const data = (await resp.json()) as { ok: boolean; error?: string } & T;
  if (!data.ok) throw new Error(`Slack API ${method} error: ${data.error}`);
  return data;
}

async function slackPost<T = unknown>(
  token: string,
  method: string,
  body: Record<string, unknown>,
): Promise<T> {
  const resp = await proxiedFetch(`${SLACK_API}/${method}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json; charset=utf-8',
    },
    body: JSON.stringify(body),
  });
  if (!resp.ok) throw new Error(`Slack POST ${method} → HTTP ${resp.status}`);
  const data = (await resp.json()) as { ok: boolean; error?: string } & T;
  if (!data.ok) throw new Error(`Slack POST ${method} error: ${data.error}`);
  return data;
}

// ─── Slack API response shapes (minimal) ────────────────────────────────────

interface SlackChannel {
  id: string;
  name: string;
  is_private: boolean;
  is_member?: boolean;
  num_members?: number;
  latest?: { ts: string };
  is_archived?: boolean;
  topic?: { value: string };
  purpose?: { value: string };
}

/** Richer channel descriptor returned by listAllChannels() and used in the Settings UI. */
export interface SlackChannelInfo {
  id: string;
  name: string;
  isPrivate: boolean;
  /** True if the bot token is already a member of this channel. */
  isMember: boolean;
  memberCount?: number;
  topic?: string;
  purpose?: string;
}

interface SlackUser {
  id: string;
  name: string;
  real_name?: string;
  profile?: {
    display_name?: string;
    real_name?: string;
    email?: string;
    image_48?: string;
  };
  is_bot?: boolean;
  deleted?: boolean;
}

interface SlackMessage {
  type: string;
  ts: string;
  user?: string;
  bot_id?: string;
  username?: string;
  text: string;
  thread_ts?: string;
  reply_count?: number;
  reactions?: { name: string; count: number; users: string[] }[];
  subtype?: string;
}

// ─── SlackService ─────────────────────────────────────────────────────────────

export class SlackService {
  private static instance: SlackService;

  private constructor() {}

  static getInstance(): SlackService {
    if (!SlackService.instance) SlackService.instance = new SlackService();
    return SlackService.instance;
  }

  // ── Auth ──────────────────────────────────────────────────────────────────

  /** Test a bot token and return workspace metadata. */
  async testToken(token: string): Promise<{ teamId: string; teamName: string; botUserId: string }> {
    const data = await slackGet<{
      team_id: string;
      team?: { name: string };
      user_id: string;
    }>(token, 'auth.test');
    return {
      teamId: data.team_id,
      teamName: data.team?.name ?? data.team_id,
      botUserId: data.user_id,
    };
  }

  /** Save settings and validate the token. */
  async connect(token: string): Promise<SlackSettings> {
    const meta = await this.testToken(token);
    const settings: SlackSettings = {
      accessToken: token,
      teamId: meta.teamId,
      teamName: meta.teamName,
      botUserId: meta.botUserId,
      monitoredChannelIds: [],
      enabled: true,
    };
    await saveSlackSettings(settings);
    return settings;
  }

  async disconnect(): Promise<void> {
    await saveSlackSettings(null);
  }

  async getSettings(): Promise<SlackSettings | null> {
    return getSlackSettings();
  }

  // ── Channels ──────────────────────────────────────────────────────────────

  /**
   * List ALL public channels in the workspace plus any private channels the
   * bot is already a member of. The `isMember` field indicates whether the
   * bot token is currently in the channel (from Slack's `is_member` field).
   * Requires: channels:read, groups:read
   */
  async listAllChannels(token: string): Promise<SlackChannelInfo[]> {
    const results: SlackChannelInfo[] = [];

    // Fetch public channels (all, regardless of membership)
    let cursor: string | undefined;
    do {
      const params: Record<string, string> = {
        types: 'public_channel',
        exclude_archived: 'true',
        limit: '200',
      };
      if (cursor) params.cursor = cursor;
      const data = await slackGet<{
        channels: SlackChannel[];
        response_metadata?: { next_cursor?: string };
      }>(token, 'conversations.list', params);
      for (const ch of data.channels) {
        if (ch.is_archived) continue;
        results.push({
          id: ch.id,
          name: ch.name,
          isPrivate: false,
          isMember: !!ch.is_member,
          memberCount: ch.num_members,
          topic: ch.topic?.value || undefined,
          purpose: ch.purpose?.value || undefined,
        });
      }
      cursor = data.response_metadata?.next_cursor || undefined;
    } while (cursor);

    // Fetch private channels (only ones the bot is already in)
    cursor = undefined;
    do {
      const params: Record<string, string> = {
        types: 'private_channel',
        exclude_archived: 'true',
        limit: '200',
      };
      if (cursor) params.cursor = cursor;
      const data = await slackGet<{
        channels: SlackChannel[];
        response_metadata?: { next_cursor?: string };
      }>(token, 'conversations.list', params);
      for (const ch of data.channels) {
        if (ch.is_archived) continue;
        results.push({
          id: ch.id,
          name: ch.name,
          isPrivate: true,
          isMember: true, // private channels only appear if the bot is in them
          memberCount: ch.num_members,
          topic: ch.topic?.value || undefined,
          purpose: ch.purpose?.value || undefined,
        });
      }
      cursor = data.response_metadata?.next_cursor || undefined;
    } while (cursor);

    return results.sort((a, b) => a.name.localeCompare(b.name));
  }

  /**
   * Join a public channel so the bot can read its messages.
   * Requires: channels:join
   */
  async joinChannel(token: string, channelId: string): Promise<void> {
    await slackPost(token, 'conversations.join', { channel: channelId });
  }

  /**
   * Leave a channel (public or private).
   * Requires: channels:leave / groups:leave
   */
  async leaveChannel(token: string, channelId: string): Promise<void> {
    await slackPost(token, 'conversations.leave', { channel: channelId });
  }

  /** List all public + bot-joined private channels in the workspace. */
  async listChannels(token: string): Promise<MessagingChannel[]> {
    const channels: MessagingChannel[] = [];
    let cursor: string | undefined;

    do {
      const params: Record<string, string> = {
        types: 'public_channel,private_channel',
        exclude_archived: 'true',
        limit: '200',
      };
      if (cursor) params.cursor = cursor;

      const data = await slackGet<{
        channels: SlackChannel[];
        response_metadata?: { next_cursor?: string };
      }>(token, 'conversations.list', params);

      for (const ch of data.channels) {
        if (ch.is_archived) continue;
        channels.push({
          id: ch.id,
          name: ch.name,
          platform: 'slack',
          isPrivate: ch.is_private,
          memberCount: ch.num_members,
          lastMessageAt: ch.latest?.ts
            ? new Date(parseFloat(ch.latest.ts) * 1000).toISOString()
            : undefined,
        });
      }

      cursor = data.response_metadata?.next_cursor || undefined;
    } while (cursor);

    return channels;
  }

  // ── Members ───────────────────────────────────────────────────────────────

  /** Fetch all workspace members (excluding bots and deleted users). */
  async listMembers(token: string): Promise<MessagingMember[]> {
    const members: MessagingMember[] = [];
    let cursor: string | undefined;

    do {
      const params: Record<string, string> = { limit: '200' };
      if (cursor) params.cursor = cursor;

      const data = await slackGet<{
        members: SlackUser[];
        response_metadata?: { next_cursor?: string };
      }>(token, 'users.list', params);

      for (const u of data.members) {
        if (u.deleted) continue;
        members.push({
          id: u.id,
          platform: 'slack',
          displayName: u.profile?.display_name || u.profile?.real_name || u.real_name || u.name,
          email: u.profile?.email,
          avatarUrl: u.profile?.image_48,
          isBot: !!u.is_bot,
        });
      }

      cursor = data.response_metadata?.next_cursor || undefined;
    } while (cursor);

    return members;
  }

  // ── Message Polling ───────────────────────────────────────────────────────

  /**
   * Poll a single channel for messages newer than `oldestTs`.
   * Returns parsed messages and the latest ts seen (use as next `oldestTs`).
   */
  async pollChannel(
    token: string,
    channelId: string,
    oldestTs?: string,
    memberMap: Map<string, string> = new Map(),
  ): Promise<{ messages: MessagingMessage[]; latestTs: string | undefined }> {
    const params: Record<string, string> = {
      channel: channelId,
      limit: String(PAGE_LIMIT),
    };
    if (oldestTs) params.oldest = oldestTs;

    const data = await slackGet<{
      messages: SlackMessage[];
      has_more: boolean;
    }>(token, 'conversations.history', params);

    const messages: MessagingMessage[] = [];
    let latestTs: string | undefined;
    const threadParents: SlackMessage[] = [];

    for (const m of data.messages) {
      if (m.subtype) continue; // skip channel_join, bot_message subtypes we don't need
      const authorId = m.user ?? m.bot_id ?? 'unknown';
      const authorName = memberMap.get(authorId) ?? m.username ?? authorId;
      const ts = m.ts;
      const isoTs = new Date(parseFloat(ts) * 1000).toISOString();

      if (!latestTs || ts > latestTs) latestTs = ts;

      const reactions: MessageReaction[] | undefined = m.reactions?.map(r => ({
        emoji: r.name,
        count: r.count,
        userIds: r.users,
      }));

      messages.push({
        id: ts,
        platform: 'slack',
        channelId,
        authorId,
        authorName,
        text: m.text,
        timestamp: isoTs,
        threadId: m.thread_ts !== ts ? m.thread_ts : undefined,
        isReply: !!m.thread_ts && m.thread_ts !== ts,
        replyCount: m.reply_count,
        reactions,
      });

      // Collect thread parents that have replies to fetch separately
      if (m.reply_count && m.reply_count > 0 && (!m.thread_ts || m.thread_ts === ts)) {
        threadParents.push(m);
      }
    }

    // Fetch replies for each threaded message
    for (const parent of threadParents) {
      try {
        const threadParams: Record<string, string> = {
          channel: channelId,
          ts: parent.ts,
          limit: '100',
        };
        const threadData = await slackGet<{ messages: SlackMessage[] }>(
          token, 'conversations.replies', threadParams,
        );

        // Index 0 is the parent (already added), process the rest.
        // Note: reply timestamps must NOT update latestTs — the cursor is
        // based solely on conversations.history timestamps so that future
        // history polls don't skip over channel messages.
        for (const reply of threadData.messages.slice(1)) {
          if (reply.subtype) continue;
          const replyAuthorId = reply.user ?? reply.bot_id ?? 'unknown';
          const replyAuthorName = memberMap.get(replyAuthorId) ?? reply.username ?? replyAuthorId;
          const replyTs = reply.ts;
          const replyIsoTs = new Date(parseFloat(replyTs) * 1000).toISOString();

          messages.push({
            id: replyTs,
            platform: 'slack',
            channelId,
            authorId: replyAuthorId,
            authorName: replyAuthorName,
            text: reply.text,
            timestamp: replyIsoTs,
            threadId: parent.ts,
            isReply: true,
            reactions: reply.reactions?.map(r => ({ emoji: r.name, count: r.count, userIds: r.users })),
          });
        }
      } catch (err) {
        console.warn(`[SlackService] Failed to fetch thread replies for ${parent.ts}:`, err);
      }
    }

    return { messages, latestTs };
  }

  /**
   * Full poll cycle: iterate all monitored channels, collect new messages,
   * persist them, and return a snapshot for analysis.
   */
  async pollAll(
    settings: SlackSettings,
    channelCursors: Map<string, string>,
    memberMap: Map<string, string>,
  ): Promise<{ allMessages: MessagingMessage[]; updatedCursors: Map<string, string>; channelNameMap: Map<string, string> }> {
    const allMessages: MessagingMessage[] = [];
    const updatedCursors = new Map(channelCursors);

    // Fetch channel list to build id → name map for display
    const allChannels = await this.listChannels(settings.accessToken).catch(() => [] as { id: string; name: string }[]);
    const channelNameMap = new Map(allChannels.map(c => [c.id, c.name]));

    const channelIds = settings.monitoredChannelIds.length > 0
      ? settings.monitoredChannelIds
      : allChannels.map(c => c.id);

    for (const channelId of channelIds) {
      try {
        const oldestTs = channelCursors.get(channelId);
        const { messages, latestTs } = await this.pollChannel(
          settings.accessToken,
          channelId,
          oldestTs,
          memberMap,
        );
        allMessages.push(...messages);
        if (latestTs) updatedCursors.set(channelId, latestTs);

        // Reaction refresh: re-fetch the last 50 messages without a cursor
        // to overwrite already-stored messages with up-to-date reaction data.
        // saveMessages uses put() (upsert) so this is safe and idempotent.
        try {
          const refreshData = await slackGet<{ messages: SlackMessage[] }>(
            settings.accessToken, 'conversations.history',
            { channel: channelId, limit: '50' },
          );
          const topLevel = refreshData.messages.filter(m => !m.subtype);
          const refreshed: MessagingMessage[] = topLevel.map(m => ({
            id: m.ts,
            platform: 'slack' as const,
            channelId,
            authorId: m.user ?? m.bot_id ?? 'unknown',
            authorName: memberMap.get(m.user ?? m.bot_id ?? 'unknown') ?? m.username ?? (m.user ?? m.bot_id ?? 'unknown'),
            text: m.text,
            timestamp: new Date(parseFloat(m.ts) * 1000).toISOString(),
            threadId: m.thread_ts !== m.ts ? m.thread_ts : undefined,
            isReply: !!m.thread_ts && m.thread_ts !== m.ts,
            replyCount: m.reply_count,
            reactions: m.reactions?.map(r => ({ emoji: r.name, count: r.count, userIds: r.users })),
          }));
          if (refreshed.length > 0) await saveMessages(refreshed);

          // Thread sync: read ALL stored parent messages for today from IndexedDB
          // (not just the last 50 from history - parent may be buried deeper)
          const today = toLocalDateStr();
          const storedToday = await getMessagesByDay('slack', today);
          const storedParents = storedToday.filter(
            m => m.channelId === channelId && !m.isReply && (m.replyCount ?? 0) > 0,
          );
          for (const parent of storedParents) {
            try {
              const threadData = await slackGet<{ messages: SlackMessage[] }>(
                settings.accessToken, 'conversations.replies',
                { channel: channelId, ts: parent.id, limit: '100' },
              );
              const replies: MessagingMessage[] = threadData.messages.slice(1)
                .filter(r => !r.subtype)
                .map(r => ({
                  id: r.ts,
                  platform: 'slack' as const,
                  channelId,
                  authorId: r.user ?? r.bot_id ?? 'unknown',
                  authorName: memberMap.get(r.user ?? r.bot_id ?? 'unknown') ?? r.username ?? (r.user ?? r.bot_id ?? 'unknown'),
                  text: r.text,
                  timestamp: new Date(parseFloat(r.ts) * 1000).toISOString(),
                  threadId: parent.id,
                  isReply: true,
                  reactions: r.reactions?.map(rx => ({ emoji: rx.name, count: rx.count, userIds: rx.users })),
                }));
              if (replies.length > 0) await saveMessages(replies);
            } catch (replyErr) {
              console.warn(`[SlackService] thread replies fetch for ${parent.id} in ${channelId} failed:`, replyErr);
            }
          }
        } catch (refreshErr) {
          console.warn(`[SlackService] reaction refresh for ${channelId} failed:`, refreshErr);
        }
      } catch (err) {
        console.warn(`[SlackService] pollChannel ${channelId} failed:`, err);
      }
    }

    return { allMessages, updatedCursors, channelNameMap };
  }

  // ── Bot Posting ───────────────────────────────────────────────────────────

  /** Post a plain-text or mrkdwn-formatted message to a channel. */
  async postMessage(token: string, channelId: string, text: string): Promise<void> {
    await slackPost(token, 'chat.postMessage', { channel: channelId, text });
  }

  /** Post a structured Block Kit message (for rich summaries). */
  async postBlocks(
    token: string,
    channelId: string,
    text: string,
    blocks: unknown[],
  ): Promise<void> {
    await slackPost(token, 'chat.postMessage', { channel: channelId, text, blocks });
  }

  // ── Analytics ─────────────────────────────────────────────────────────────

  /** Build per-member and per-channel stats from a flat message list. */
  buildSnapshot(
    messages: MessagingMessage[],
    date: string,
    memberNameMap: Map<string, string> = new Map(),
    channelNameMap: Map<string, string> = new Map(),
  ): Omit<MessagingSnapshot, 'anomalies'> {
    const resolveName = (id: string, fallback: string) =>
      memberNameMap.get(id) || (fallback !== id ? fallback : id);
    const resolveChannel = (id: string) => channelNameMap.get(id) ?? id;

    const memberMap = new Map<string, MemberActivityStats>();
    const channelMap = new Map<string, ChannelActivityStats>();
    const memberChannels = new Map<string, Set<string>>(); // memberId → unique channelIds

    for (const msg of messages) {
      const day = new Date(msg.timestamp).toLocaleDateString('en-CA');

      // Member stats
      let ms = memberMap.get(msg.authorId);
      if (!ms) {
        ms = {
          memberId: msg.authorId,
          memberName: resolveName(msg.authorId, msg.authorName),
          platform: 'slack',
          messageCount: 0,
          replyCount: 0,
          totalReactions: 0,
          activeChannelCount: 0,
          dailyCounts: {},
        };
        memberMap.set(msg.authorId, ms);
      }
      ms.messageCount++;
      if (msg.isReply) ms.replyCount++;
      ms.dailyCounts[day] = (ms.dailyCounts[day] ?? 0) + 1;
      if (!memberChannels.has(msg.authorId)) memberChannels.set(msg.authorId, new Set());
      memberChannels.get(msg.authorId)!.add(msg.channelId);
      if (!ms.lastActiveAt || msg.timestamp > ms.lastActiveAt) ms.lastActiveAt = msg.timestamp;
      // Channel stats
      let cs = channelMap.get(msg.channelId);
      if (!cs) {
        cs = {
          channelId: msg.channelId,
          channelName: resolveChannel(msg.channelId),
          platform: 'slack',
          messageCount: 0,
          uniqueAuthors: 0,
          totalReactions: 0,
          dailyCounts: {},
          topContributors: [],
        };
        channelMap.set(msg.channelId, cs);
      }
      cs.messageCount++;
      cs.dailyCounts[day] = (cs.dailyCounts[day] ?? 0) + 1;
      if (!cs.lastMessageAt || msg.timestamp > cs.lastMessageAt) cs.lastMessageAt = msg.timestamp;
      if (msg.reactions) cs.totalReactions += msg.reactions.reduce((s, r) => s + r.count, 0);

      // Count reaction participants as active members and credit their given reactions
      if (msg.reactions) {
        for (const reaction of msg.reactions) {
          for (const userId of (reaction.userIds ?? [])) {
            if (!memberMap.has(userId)) {
              memberMap.set(userId, {
                memberId: userId,
                memberName: memberNameMap.get(userId) ?? userId,
                platform: 'slack',
                messageCount: 0,
                replyCount: 0,
                totalReactions: 0,
                activeChannelCount: 0,
                dailyCounts: {},
              });
            }
            // +1 for each reaction this user gave
            memberMap.get(userId)!.totalReactions += 1;
          }
        }
      }
    }
    // Assign unique channel counts from sets accumulated during loop
    for (const [memberId, channels] of memberChannels) {
      const ms = memberMap.get(memberId);
      if (ms) ms.activeChannelCount = channels.size;
    }
    // Compute total reactions across all messages
    let totalReactions = 0;
    for (const msg of messages) {
      if (msg.reactions) totalReactions += msg.reactions.reduce((s, r) => s + r.count, 0);
    }

    for (const [channelId, cs] of channelMap) {
      const authors = new Map<string, number>();
      for (const msg of messages) {
        if (msg.channelId !== channelId) continue;
        authors.set(msg.authorId, (authors.get(msg.authorId) ?? 0) + 1);
      }
      cs.uniqueAuthors = authors.size;
      cs.topContributors = [...authors.entries()]
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([id, count]) => ({
          memberId: id,
          memberName: memberMap.get(id)?.memberName ?? memberNameMap.get(id) ?? id,
          count,
        }));
    }

    return {
      platform: 'slack',
      date,
      totalMessages: messages.length,
      totalReactions,
      activeMembers: memberMap.size,
      activeChannels: channelMap.size,
      memberStats: [...memberMap.values()],
      channelStats: [...channelMap.values()],
      quietMembers: [],
    };
  }

  /** Detect anomalies in today's snapshot vs. a historical baseline. */
  detectAnomalies(
    today: Omit<MessagingSnapshot, 'anomalies'>,
    baseline: Omit<MessagingSnapshot, 'anomalies'> | null,
    messages: MessagingMessage[] = [],
  ): MessagingAnomaly[] {
    const anomalies: MessagingAnomaly[] = [];
    const now = new Date().toISOString();

    // Zero-activity channels (normally active but silent today)
    if (baseline) {
      for (const baseCs of baseline.channelStats) {
        const todayCs = today.channelStats.find(c => c.channelId === baseCs.channelId);
        if (!todayCs && baseCs.messageCount > 5) {
          anomalies.push({
            type: 'no_activity',
            severity: baseCs.messageCount > 20 ? 'high' : 'medium',
            platform: 'slack',
            channelId: baseCs.channelId,
            channelName: baseCs.channelName,
            description: `#${baseCs.channelName} had no messages today (baseline: ${baseCs.messageCount} msgs/day).`,
            detectedAt: now,
          });
        }
      }

      // Sudden spikes (>3× baseline)
      for (const cs of today.channelStats) {
        const base = baseline.channelStats.find(b => b.channelId === cs.channelId);
        if (base && cs.messageCount > base.messageCount * 3 && cs.messageCount > 20) {
          anomalies.push({
            type: 'sudden_spike',
            severity: 'medium',
            platform: 'slack',
            channelId: cs.channelId,
            channelName: cs.channelName,
            description: `#${cs.channelName} had ${cs.messageCount} messages today (${Math.round(cs.messageCount / base.messageCount)}× normal).`,
            detectedAt: now,
          });
        }
      }

      // Sudden silence: members who were active yesterday but have zero today
      for (const baseMs of baseline.memberStats) {
        const todayMs = today.memberStats.find(m => m.memberId === baseMs.memberId);
        if (!todayMs && baseMs.messageCount > 3) {
          anomalies.push({
            type: 'sudden_silence',
            severity: 'low',
            platform: 'slack',
            memberId: baseMs.memberId,
            memberName: baseMs.memberName,
            description: `${baseMs.memberName} was active yesterday (${baseMs.messageCount} msgs) but has not posted today.`,
            detectedAt: now,
          });
        }
      }
    }

    // Single-author channels
    for (const cs of today.channelStats) {
      if (cs.uniqueAuthors === 1 && cs.messageCount > 5) {
        anomalies.push({
          type: 'single_author_channel',
          severity: 'low',
          platform: 'slack',
          channelId: cs.channelId,
          channelName: cs.channelName,
          description: `#${cs.channelName} only had 1 active participant today (${cs.messageCount} messages).`,
          detectedAt: now,
        });
      }
    }

    // Conflict / sentiment analysis
    anomalies.push(...detectSentimentAnomalies(messages, 'slack', today.channelStats));

    return anomalies;
  }
}

export const slackService = SlackService.getInstance();
